/* De projectlijst (/crm/projects): één compacte, doorzoekbare lijst.
 *
 *   /           zoeken
 *   ↑ ↓ (j k)   door de lijst
 *   Enter       project openen (cmd-klik op een rij: nieuw tabblad)
 *
 * Stadiumtabs tellen aantal en waarde binnen je zoekterm. Focusprojecten staan als
 * chips bovenaan. Per project: laatste activiteit, open taken, live EMS en de stand
 * van de laatste offerte. Naast de lijst een bord per stadium. De volgorde die je
 * ziet, gebruikt de projectpagina voor vorige/volgende. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Columns3, FolderSync, Info, List, MoreHorizontal, Plus, Search, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ProjectQuickActions } from '@/components/crm/project/ProjectQuickActions';
import { getStageConfig } from '@/components/crm/project/projectUtils';
import { cn } from '@/lib/utils';
import type { Signalen } from './data/signalen';
import {
  activiteit, bewaarVolgorde, filterEnSorteer, relatief, STADIA, tellingen,
  type LijstFilter, type LijstProject, type Sortering, type Stadium, type StatusFilter,
} from './data/lijst';

const EURO = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const euro = (v: number | null | undefined) => (v ? EURO.format(v) : '—');
const euroKort = (v: number) =>
  v >= 1_000_000 ? `€ ${(v / 1_000_000).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} mln` : v >= 1000 ? `€ ${Math.round(v / 1000)}k` : v ? EURO.format(v) : '';

const OFFERTE: Record<string, string> = { draft: 'offerte concept', sent: 'offerte verstuurd', accepted: 'offerte akkoord', declined: 'offerte afgewezen', expired: 'offerte verlopen' };
const STIP: Record<string, string> = { verkennen: 'bg-blue-600', voorstel: 'bg-purple-600', gewonnen: 'bg-green-600', operatie: 'bg-emerald-700' };

export type Weergave = 'lijst' | 'bord';

interface Props {
  projecten: LijstProject[];
  signalen: Map<string, Signalen>;
  laden: boolean;
  filter: LijstFilter;
  onFilter: (f: LijstFilter) => void;
  weergave: Weergave;
  onWeergave: (w: Weergave) => void;
  focus: Set<string>;
  onFocus: (id: string) => void;
  onNieuw: () => void;
  onBewerk: (p: LijstProject) => void;
  onArchiveer: (id: string) => Promise<void>;
  onSync: () => void;
  bezigMetSync: boolean;
  onSiteInfo: () => void;
}

function Stadium({ stage }: { stage: string | null }) {
  const c = getStageConfig(stage);
  return <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', c.bg, c.text)}>{c.label}</span>;
}

function Meta({ p, s }: { p: LijstProject; s: Signalen | undefined }) {
  const delen = [
    p.organization?.name && p.organization.name !== p.name ? p.organization.name : null,
    s?.openTaken ? `${s.openTaken} open ${s.openTaken === 1 ? 'taak' : 'taken'}` : null,
    s?.offerte ? OFFERTE[s.offerte] ?? `offerte ${s.offerte}` : null,
  ].filter(Boolean);
  if (!delen.length && !s?.ems) return null;
  return (
    <span className="flex min-w-0 items-center gap-1.5 truncate text-xs text-muted-foreground">
      {s?.ems && (
        <span className="inline-flex shrink-0 items-center gap-1 font-medium text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />EMS
        </span>
      )}
      {s?.ems && delen.length > 0 && <span aria-hidden>·</span>}
      <span className="truncate">{delen.join(' · ')}</span>
    </span>
  );
}

function Initialen({ naam }: { naam: string | null | undefined }) {
  const i = (naam ?? '?').split(/\s+/).map((d) => d[0]).join('').slice(0, 2).toUpperCase();
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 text-[11px] font-semibold text-white" title={naam ?? undefined}>
      {i}
    </span>
  );
}

const KOLOMMEN = 'grid-cols-[40px_minmax(0,1fr)] sm:grid-cols-[52px_minmax(0,1fr)_104px] md:grid-cols-[52px_minmax(0,1fr)_110px_110px_90px] lg:grid-cols-[56px_minmax(0,1fr)_110px_110px_60px_100px_36px]';

export function ProjectLijst(props: Props) {
  const { projecten, signalen, laden, filter, onFilter, weergave, focus } = props;
  const navigate = useNavigate();
  const zoekRef = useRef<HTMLInputElement>(null);
  const rijRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [actief, setActief] = useState(-1);

  const zichtbaar = useMemo(() => filterEnSorteer(projecten, filter, signalen), [projecten, filter, signalen]);
  const tabs = useMemo(() => tellingen(projecten, filter), [projecten, filter]);
  const focusProjecten = projecten.filter((p) => focus.has(p.id));

  useEffect(() => bewaarVolgorde(zichtbaar.map((p) => p.id)), [zichtbaar]);
  useEffect(() => setActief(-1), [filter.zoek, filter.stadium, filter.status]);
  useEffect(() => {
    if (actief >= 0) rijRefs.current[actief]?.scrollIntoView({ block: 'nearest' });
  }, [actief]);

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement;
      const typen = doel.tagName === 'INPUT' || doel.tagName === 'TEXTAREA' || doel.isContentEditable;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!typen && e.key === '/') {
        e.preventDefault();
        zoekRef.current?.focus();
        return;
      }
      if (typen && doel !== zoekRef.current) return;
      if (weergave !== 'lijst') return;
      if (e.key === 'ArrowDown' || (!typen && e.key === 'j')) {
        e.preventDefault();
        if (typen) zoekRef.current?.blur();
        setActief((a) => Math.min(zichtbaar.length - 1, a + 1));
      } else if (e.key === 'ArrowUp' || (!typen && e.key === 'k')) {
        e.preventDefault();
        setActief((a) => Math.max(0, a - 1));
      } else if (e.key === 'Enter' && zichtbaar.length) {
        const p = zichtbaar[actief >= 0 ? actief : 0];
        if (p && (typen || actief >= 0)) {
          e.preventDefault();
          navigate(`/crm/projects/${p.id}`);
        }
      } else if (e.key === 'Escape' && typen) {
        onFilter({ ...filter, zoek: '' });
        zoekRef.current?.blur();
      }
    };
    window.addEventListener('keydown', toets);
    return () => window.removeEventListener('keydown', toets);
  }, [zichtbaar, actief, weergave, filter, onFilter, navigate]);

  const sorteer = (s: Sortering) =>
    onFilter({ ...filter, sortering: s, oplopend: filter.sortering === s ? !filter.oplopend : s === 'naam' || s === 'stadium' });

  const Kop = ({ s, children, rechts }: { s: Sortering; children: React.ReactNode; rechts?: boolean }) => (
    <button
      type="button"
      onClick={() => sorteer(s)}
      className={cn('inline-flex items-center gap-1 hover:text-foreground', rechts && 'justify-end', filter.sortering === s && 'font-semibold text-foreground')}
    >
      {children}
      {filter.sortering === s && (filter.oplopend ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
    </button>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Werkbalk */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <h1 className="mr-2 text-2xl font-semibold tracking-tight">Projecten</h1>
        <label className="relative order-last w-full sm:order-none sm:ml-auto sm:w-72">
          <span className="sr-only">Zoek project</span>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            ref={zoekRef}
            value={filter.zoek}
            onChange={(e) => onFilter({ ...filter, zoek: e.target.value })}
            placeholder="Zoek op nummer, naam of klant"
            className="h-9 pl-8 pr-8"
          />
          <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border bg-muted px-1.5 font-mono text-[11px] text-muted-foreground sm:block">/</kbd>
        </label>
        <div role="group" aria-label="Weergave" className="ml-auto flex rounded-lg border p-0.5 sm:ml-0">
          {([['lijst', List, 'Lijst'], ['bord', Columns3, 'Bord']] as const).map(([w, Icoon, label]) => (
            <button
              key={w}
              type="button"
              aria-pressed={weergave === w}
              onClick={() => props.onWeergave(w)}
              className={cn('flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm', weergave === w ? 'bg-muted font-medium' : 'text-muted-foreground hover:text-foreground')}
            >
              <Icoon className="h-4 w-4" aria-hidden />
              <span className="hidden md:inline">{label}</span>
            </button>
          ))}
        </div>
        <Button size="sm" onClick={props.onNieuw} className="h-9">
          <Plus className="mr-1.5 h-4 w-4" />
          <span className="hidden sm:inline">Nieuw project</span>
          <span className="sm:hidden">Nieuw</span>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Meer acties">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={props.onSync} disabled={props.bezigMetSync}>
              <FolderSync className={cn('mr-2 h-4 w-4', props.bezigMetSync && 'animate-spin')} />
              SharePoint-mappen controleren
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={props.onSiteInfo}><Info className="mr-2 h-4 w-4" />SharePoint-site info</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stadia en status */}
      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="Stadium" className="-mx-3 flex w-[calc(100%+1.5rem)] gap-1.5 overflow-x-auto px-3 [scrollbar-width:none] sm:mx-0 sm:w-auto sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
          {tabs.map((t) => {
            const aan = filter.stadium === t.stadium;
            return (
              <button
                key={t.stadium}
                type="button"
                role="tab"
                aria-selected={aan}
                onClick={() => onFilter({ ...filter, stadium: t.stadium as Stadium | 'alle' })}
                className={cn(
                  'flex h-9 shrink-0 items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
                  aan ? 'border-foreground bg-foreground text-background' : 'bg-background hover:bg-muted',
                )}
              >
                {t.stadium !== 'alle' && <span className={cn('h-2 w-2 rounded-full', STIP[t.stadium])} aria-hidden />}
                <span className="font-medium">{t.stadium === 'alle' ? 'Alle' : getStageConfig(t.stadium).label}</span>
                <span className={cn('tabular-nums', aan ? 'opacity-80' : 'text-muted-foreground')}>{t.aantal}</span>
                {t.waarde > 0 && <span className={cn('hidden tabular-nums lg:inline', aan ? 'opacity-70' : 'text-muted-foreground')}>· {euroKort(t.waarde)}</span>}
              </button>
            );
          })}
        </div>
        <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
          <span>Toon</span>
          <select
            value={filter.status}
            onChange={(e) => onFilter({ ...filter, status: e.target.value as StatusFilter })}
            className="h-9 rounded-md border bg-background px-2 text-foreground"
          >
            <option value="lopend">Lopend</option>
            <option value="afgesloten">Afgesloten</option>
            <option value="alles">Alles</option>
          </select>
        </label>
      </div>

      {/* Focus */}
      {focusProjecten.length > 0 && (
        <div className="-mx-3 flex items-center gap-2 overflow-x-auto px-3 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />Focus
          </span>
          {focusProjecten.map((p) => (
            <Link
              key={p.id}
              to={`/crm/projects/${p.id}`}
              className="flex h-8 shrink-0 items-center gap-2 rounded-full border bg-background px-3 text-sm hover:border-foreground/40"
            >
              <span className={cn('h-2 w-2 rounded-full', STIP[p.stage ?? ''] ?? 'bg-slate-400')} aria-hidden />
              {p.project_number && <span className="font-mono text-xs text-muted-foreground">{p.project_number}</span>}
              <span className="max-w-[220px] truncate font-medium">{p.name}</span>
            </Link>
          ))}
        </div>
      )}

      {laden ? (
        <div className="flex flex-col gap-2 rounded-xl border p-4" aria-busy>
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-9 animate-pulse rounded bg-muted" />)}
        </div>
      ) : weergave === 'bord' ? (
        <Bord projecten={zichtbaar} signalen={signalen} focus={focus} filter={filter} />
      ) : zichtbaar.length === 0 ? (
        <div className="rounded-xl border px-5 py-10 text-center text-sm text-muted-foreground">
          Geen projecten gevonden.{' '}
          {(filter.zoek || filter.stadium !== 'alle' || filter.status !== 'lopend') && (
            <button type="button" className="font-medium text-foreground underline" onClick={() => onFilter({ ...filter, zoek: '', stadium: 'alle', status: 'alles' })}>
              Toon alles
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-background">
          <div className="flex items-center border-b bg-muted/40 text-xs text-muted-foreground">
            <span className="w-9 shrink-0" />
            <div className={cn('grid flex-1 items-center gap-3 py-2 pr-2', KOLOMMEN)}>
              <Kop s="nummer">Nr</Kop>
              <Kop s="naam">Project</Kop>
              <span className="hidden md:block"><Kop s="stadium">Stadium</Kop></span>
              <span className="hidden text-right md:block"><Kop s="waarde" rechts>Waarde</Kop></span>
              <span className="hidden text-right lg:block">Kans</span>
              <span className="hidden text-right sm:block"><Kop s="activiteit" rechts>Activiteit</Kop></span>
              <span className="hidden lg:block" />
            </div>
            <span className="w-10 shrink-0" />
          </div>
          <ul className="divide-y">
            {zichtbaar.map((p, i) => {
              const s = signalen.get(p.id);
              const inVerkoop = p.stage === 'verkennen' || p.stage === 'voorstel';
              return (
                <li key={p.id} className={cn('group flex items-center', i === actief && 'bg-muted')}>
                  <button
                    type="button"
                    onClick={() => props.onFocus(p.id)}
                    aria-label={focus.has(p.id) ? `${p.name} uit focus halen` : `${p.name} in focus zetten`}
                    aria-pressed={focus.has(p.id)}
                    className="flex h-10 w-9 shrink-0 items-center justify-center"
                  >
                    <Star className={cn('h-4 w-4', focus.has(p.id) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40 group-hover:text-muted-foreground')} />
                  </button>
                  <Link
                    ref={(el) => (rijRefs.current[i] = el)}
                    to={`/crm/projects/${p.id}`}
                    onMouseEnter={() => setActief(i)}
                    className={cn('grid flex-1 items-center gap-3 py-2.5 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring', KOLOMMEN)}
                  >
                    <span className="font-mono text-xs text-muted-foreground">{p.project_number ?? '—'}</span>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">{p.name}</span>
                      <Meta p={p} s={s} />
                    </span>
                    <span className="hidden md:block"><Stadium stage={p.stage} /></span>
                    <span className="hidden text-right tabular-nums md:block">{euro(p.amount)}</span>
                    <span className="hidden text-right tabular-nums text-muted-foreground lg:block">{inVerkoop && p.probability != null ? `${p.probability}%` : ''}</span>
                    <span className="hidden text-right text-xs text-muted-foreground sm:block md:text-sm" title={activiteit(p, s)}>{relatief(activiteit(p, s))}</span>
                    <span className="hidden justify-end lg:flex"><Initialen naam={p.owner?.display_name ?? p.owner?.email} /></span>
                  </Link>
                  <span className="flex w-10 shrink-0 justify-center">
                    <ProjectQuickActions project={p as any} onEdit={props.onBewerk as any} onDelete={props.onArchiveer} />
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">
            {zichtbaar.length} project{zichtbaar.length === 1 ? '' : 'en'} · <kbd className="font-mono">/</kbd> zoeken · <kbd className="font-mono">↑ ↓</kbd> bladeren · <kbd className="font-mono">Enter</kbd> openen
          </p>
        </div>
      )}
    </div>
  );
}

function Bord({ projecten, signalen, focus, filter }: { projecten: LijstProject[]; signalen: Map<string, Signalen>; focus: Set<string>; filter: LijstFilter }) {
  const kolommen = filter.stadium === 'alle' ? STADIA : STADIA.filter((s) => s === filter.stadium);
  return (
    <div className="-mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0">
      <div className={cn('grid min-w-[880px] items-start gap-3', kolommen.length === 1 ? 'grid-cols-1 min-w-0' : 'grid-cols-4')}>
        {kolommen.map((st) => {
          const lijst = projecten.filter((p) => p.stage === st);
          const waarde = lijst.reduce((s, p) => s + (p.amount ?? 0), 0);
          return (
            <section key={st} aria-label={getStageConfig(st).label} className="flex flex-col gap-2 rounded-xl bg-muted/50 p-2">
              <div className="flex items-center gap-2 px-1.5 py-1 text-sm">
                <span className={cn('h-2 w-2 rounded-full', STIP[st])} aria-hidden />
                <span className="font-semibold">{getStageConfig(st).label}</span>
                <span className="text-muted-foreground">{lijst.length}</span>
                {waarde > 0 && <span className="ml-auto text-xs tabular-nums text-muted-foreground">{euroKort(waarde)}</span>}
              </div>
              {lijst.map((p) => {
                const s = signalen.get(p.id);
                return (
                  <Link key={p.id} to={`/crm/projects/${p.id}`} className="flex flex-col gap-1 rounded-lg border bg-background p-3 text-sm hover:border-foreground/30">
                    <span className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-mono">{p.project_number ?? '—'}</span>
                      {focus.has(p.id) && <Star className="h-3 w-3 fill-amber-400 text-amber-400" aria-label="focus" />}
                      <span className="ml-auto">{relatief(activiteit(p, s))}</span>
                    </span>
                    <span className="line-clamp-2 font-medium leading-snug">{p.name}</span>
                    <Meta p={p} s={s} />
                    {p.amount ? <span className="text-xs font-medium tabular-nums">{euro(p.amount)}</span> : null}
                  </Link>
                );
              })}
              {lijst.length === 0 && <p className="px-2 py-3 text-xs text-muted-foreground">Geen projecten.</p>}
            </section>
          );
        })}
      </div>
    </div>
  );
}
